import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { store, persistor } from './store';
import theme from './theme';

// Components
import Login from './components/board/Login';
import Dashboard from './components/Dashboard';
import Layout from './components/Layout';
import AllMothers from './components/AllMothers';
import AllDonors from './components/AllDonors';
import DonorDashboard from './components/DonorDashboard';
import MonthlySavings from './components/MonthlySavings';
import PartnerDash from './components/PartnerDash';
import PartnerLayout from './components/board/PartnerLayout';
import PartnerDashboard from './components/board/PartnerDashboard';
import Mothers from './components/board/Mothers';
import PartnerSubscription from './components/board/PartnerSubscription';
import MatchFunding from './components/board/MatchFunding';
import PartnerDisbursements from './components/board/PartnerDisbursements';
import PartnerActivities from './components/board/Activities';
import PartnerProfile from './components/board/PartnerProfile';
import PartnerSettings from './components/board/Settings';
import ProtectedRoute from './components/ProtectedRoute';

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              
              {/* Admin Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Partners Management */}
              <Route 
                path="/partners" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <PartnerDash />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Mothers Management */}
              <Route 
                path="/mothers" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AllMothers />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/mothers/savings" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <MonthlySavings />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Donors Management */}
              <Route 
                path="/donors" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <AllDonors />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/donor-view" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Layout>
                      <DonorDashboard />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Partner Routes */}
              <Route 
                path="/partner/:partnerId" 
                element={
                  <ProtectedRoute allowedRoles={['partner']}>
                    <PartnerLayout>
                      <PartnerDashboard />
                    </PartnerLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/partner/:partnerId/mothers" 
                element={
                  <ProtectedRoute allowedRoles={['partner']}>
                    <PartnerLayout>
                      <Mothers />
                    </PartnerLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/partner/:partnerId/subscription" 
                element={
                  <ProtectedRoute allowedRoles={['partner']}>
                    <PartnerLayout>
                      <PartnerSubscription />
                    </PartnerLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/partner/:partnerId/match-funding" 
                element={
                  <ProtectedRoute allowedRoles={['partner']}>
                    <PartnerLayout>
                      <MatchFunding />
                    </PartnerLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/partner/:partnerId/contributions" 
                element={
                  <ProtectedRoute allowedRoles={['partner']}>
                    <PartnerLayout>
                      <PartnerDisbursements />
                    </PartnerLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/partner/:partnerId/activities" 
                element={
                  <ProtectedRoute allowedRoles={['partner']}>
                    <PartnerLayout>
                      <PartnerActivities />
                    </PartnerLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/partner/:partnerId/profile" 
                element={
                  <ProtectedRoute allowedRoles={['partner']}>
                    <PartnerLayout>
                      <PartnerProfile />
                    </PartnerLayout>
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/partner/:partnerId/settings" 
                element={
                  <ProtectedRoute allowedRoles={['partner']}>
                    <PartnerLayout>
                      <PartnerSettings />
                    </PartnerLayout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Default Route */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              
              {/* Unauthorized Route */}
              <Route path="/unauthorized" element={
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100vh',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <Typography variant="h4" color="error">
                    Access Denied
                  </Typography>
                  <Typography variant="body1">
                    You don't have permission to access this page.
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => window.history.back()}
                    sx={{ bgcolor: '#E91E63' }}
                  >
                    Go Back
                  </Button>
                </Box>
              } />
              
              {/* 404 Route */}
              <Route path="*" element={<div>404 - Page Not Found</div>} />
            </Routes>
          </Router>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;